import {
  from,
  fromEvent,
  map,
  type Observable,
  of,
  switchMap,
  take,
} from "rxjs"
import {
  injectCSS as injectCSSToDocument,
  removeCSS as removeCSSToDocument,
} from "./dom"

const getAttributeTokenFromString = (string: string, key: string) => {
  const regExp = new RegExp(`${key}\\s*=\\s*([^,\\s]+)`, `i`)

  return string.match(regExp)?.[1]?.trim().toLowerCase()
}

export const getAttributeValueFromString = (string: string, key: string) => {
  const token = getAttributeTokenFromString(string, key)

  if (!token) return 0

  const parsedValue = Number.parseFloat(token)

  return Number.isFinite(parsedValue) ? parsedValue : 0
}

export const injectCSSToFrame = (
  frameElement: HTMLIFrameElement,
  id: string,
  style: string,
  prepend?: boolean,
) => {
  if (!frameElement?.contentDocument?.head) return

  injectCSSToDocument(frameElement.contentDocument, id, style, prepend)
}

export const removeCSS = (frameElement: HTMLIFrameElement, id: string) => {
  if (!frameElement?.contentDocument) return

  removeCSSToDocument(frameElement.contentDocument, id)
}

export const upsertCSSToFrame = (
  frameElement: HTMLIFrameElement | undefined,
  id: string,
  style: string,
  prepend?: boolean,
) => {
  if (!frameElement) return

  const existingElement = frameElement?.contentDocument?.getElementById(
    id,
  ) as HTMLStyleElement

  if (existingElement) {
    if (existingElement.textContent !== style) {
      existingElement.textContent = style
    }
    return
  }

  injectCSSToFrame(frameElement, id, style, prepend)
}

export const getFrameViewportInfo = (frame: HTMLIFrameElement | undefined) => {
  if (frame?.contentDocument) {
    const doc = frame.contentDocument
    const viewportMetaElement = doc.querySelector(`meta[name='viewport']`)

    if (viewportMetaElement) {
      const viewportContent = viewportMetaElement.getAttribute(`content`)

      if (viewportContent) {
        const widthToken = getAttributeTokenFromString(viewportContent, `width`)
        const heightToken = getAttributeTokenFromString(
          viewportContent,
          `height`,
        )
        const width = getAttributeValueFromString(viewportContent, `width`)
        const height = getAttributeValueFromString(viewportContent, `height`)

        if (width > 0 && height > 0) {
          return {
            hasViewport: true,
            widthToken,
            heightToken,
            width: width,
            height: height,
          }
        }
        return { hasViewport: true, widthToken, heightToken }
      }
    }
  }

  return { hasViewport: false }
}

export const waitForFrameLoad = (stream: Observable<HTMLIFrameElement>) =>
  stream.pipe(
    switchMap((frame) => {
      if (
        frame.src === "about:blank" &&
        frame.contentDocument?.readyState === "complete" &&
        frame.contentDocument.body
      ) {
        return of(frame)
      }

      return fromEvent(frame, `load`).pipe(
        take(1),
        map(() => frame),
      )
    }),
  )

export const waitForFrameReady = (stream: Observable<HTMLIFrameElement>) =>
  stream.pipe(
    switchMap((frame) => {
      const readyPromise = frame?.contentDocument?.fonts.ready

      if (readyPromise) {
        return from(readyPromise).pipe(map(() => frame))
      }

      return of(frame)
    }),
  )
